import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so multiple chips for the same (job_id, date) don't
// each fire their own fetch. Key is `${job_id}::${date}`. Value is either
// a report document or `null` (meaning "no report exists").
const reportCache = new Map();       // key -> report | null
const inFlight = new Map();          // key -> Promise (dedupe concurrent lookups)

async function fetchReport(jobId, date, token) {
  const key = `${jobId}::${date}`;
  if (reportCache.has(key)) return reportCache.get(key);
  if (inFlight.has(key)) return inFlight.get(key);
  const p = axios
    .get(`${API}/api/daily-reports`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { job_id: jobId, date },
    })
    .then(resp => {
      const doc = (resp.data || [])[0] || null;
      reportCache.set(key, doc);
      return doc;
    })
    .catch(() => {
      reportCache.set(key, null);
      return null;
    })
    .finally(() => inFlight.delete(key));
  inFlight.set(key, p);
  return p;
}

// Per-service metric display config. Mirrors SERVICE_METRICS in the
// DailyReportPanel — kept in sync so a saved report renders correctly
// both in the mobile editor and here on the Timesheet card.
const METRIC_DISPLAY = {
  'Soft Dig':        { key: 'holes_dug',       short: 'holes',       long: 'Holes dug' },
  'CCTV Inspection': { key: 'lines_inspected', short: 'lines insp.', long: 'Lines Inspected' },
  'Flush/Jetting':   { key: 'lines_flushed',   short: 'lines flush.',long: 'Lines Flushed' },
};

function getMetricValue(entry) {
  const m = METRIC_DISPLAY[entry.service];
  if (!m) return null;
  const v = entry[m.key];
  return (v == null || v === '') ? null : { value: v, short: m.short, long: m.long };
}

/**
 * Small inline block that renders on a Timesheet card when the card's
 * owner is the Team Lead of that card's job. Clicking the chip expands
 * a per-service breakdown with notes, structured metrics (holes dug,
 * lines inspected, lines flushed), and equipment used.
 *
 * Props:
 *   entry       — timesheet entry with { job_id, date, user_id }
 *   token       — auth bearer
 *   compact     — if true, hide labels & tighten spacing (calendar view)
 */
export default function TeamLeadReportChip({ entry, token, compact = false }) {
  const [report, setReport] = useState(undefined); // undefined = loading; null = none
  const [expanded, setExpanded] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (!entry?.job_id || !entry?.date || !token) {
      setReport(null);
      return;
    }
    fetchReport(entry.job_id, entry.date, token).then(doc => {
      if (!alive.current) return;
      // Only surface the chip if the card's user is actually the team lead
      // on that report (defensive: the report belongs to whichever lead
      // filed it; entry.user_id may be a crew member who's *not* the lead).
      if (doc && entry.user_id && doc.team_lead_id !== entry.user_id) {
        setReport(null);
      } else {
        setReport(doc);
      }
    });
  }, [entry?.job_id, entry?.date, entry?.user_id, token]);

  if (report === undefined || report === null) return null;
  const entries = report.service_entries || [];
  if (entries.length === 0) return null;

  // Build the summary line — used both when collapsed and inline.
  // Aggregate every service's numeric metric so a mixed day (e.g. 3
  // holes + 5 lines flushed) shows both, not just one.
  const bits = [];
  for (const e of entries) {
    const m = getMetricValue(e);
    if (m) bits.push(`${m.value} ${m.short}`);
  }
  const eqCount = entries.reduce((n, e) => n + (e.equipment_used?.length || 0), 0);
  if (eqCount > 0) bits.push(`${eqCount} eq`);
  bits.push(`${entries.length} svc${entries.length === 1 ? '' : 's'}`);
  const summary = bits.join(' · ');

  return (
    <div
      className={`mt-1.5 rounded border border-amber-200 bg-amber-50 ${compact ? 'text-[10px]' : 'text-xs'} overflow-hidden`}
      data-testid={`team-lead-report-chip-${entry.job_id}-${entry.date}`}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
        className="w-full flex items-center justify-between gap-1.5 px-1.5 py-1 hover:bg-amber-100 text-left"
      >
        <span className="flex items-center gap-1 text-amber-800 font-semibold min-w-0">
          <Star className="w-3 h-3 shrink-0 fill-amber-500 text-amber-500" />
          <span className="truncate">Lead · {summary}</span>
        </span>
        {expanded
          ? <ChevronUp className="w-3 h-3 text-amber-700 shrink-0" />
          : <ChevronDown className="w-3 h-3 text-amber-700 shrink-0" />}
      </button>
      {expanded && (
        <div className="px-2 pb-2 space-y-2 border-t border-amber-200 bg-white">
          {entries.map(se => (
            <div key={se.service} className="pt-2">
              <div className="font-semibold text-slate-800">{se.service}</div>
              {se.holes_dug != null && /soft.*dig/i.test(se.service) && (
                <div className="text-slate-700">Holes dug: <span className="font-bold">{se.holes_dug}</span></div>
              )}
              {se.notes && (
                <div className="text-slate-700 italic whitespace-pre-wrap break-words">
                  {se.notes}
                </div>
              )}
              {se.equipment_used?.length > 0 && (
                <div className="text-slate-700">
                  <span className="text-slate-500">Equipment: </span>
                  {se.equipment_used.map((eq, i) => (
                    <span key={eq.id}>
                      {i > 0 && ', '}
                      <span className="font-medium">{eq.name}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="text-[10px] text-slate-400 pt-1 border-t border-amber-100">
            Filed by {report.team_lead_name} · {report.updated_at?.slice(0, 10) || ''}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Small helper the parent can call to reset the cache when the user
 * changes week or navigates away — prevents stale data on long sessions.
 */
export function clearTeamLeadReportCache() {
  reportCache.clear();
  inFlight.clear();
}
